<script setup lang="ts">
import {
  useAdminListLinkItems,
  useAdminListLinkCategories,
  useAdminDeleteLinkItem,
  useAdminReorderLinkCategories,
  useAdminReorderCategoryContents,
  getAdminListLinkItemsQueryKey,
  getAdminListLinkCategoriesQueryKey,
} from '@swipe4work/api-orval'
import { LinkItemType, type CategoryDto, type LinkItemDto } from '@swipe4work/models-orval'
import { useConfirm } from 'primevue/useconfirm'
import { useQueryClient } from '@tanstack/vue-query'
import { linkItemTypeLabel, linkItemTypeIcon } from '@/features/dashboard/linkItemTypes'
import { useAuthReady } from '@/shared/composables/UseAuthReady'
import LinkItemFormDialog from '@/features/dashboard/components/LinkItemFormDialog.vue'

definePage({
  path: '/homepage/beheer',
  name: '/homepage/beheer',
  meta: {
    title: 'Beheer',
    breadcrumbParent: '/homepage/Homepage',
    requiresGlobal: true,
  },
})

const LOOSE_SUBCATEGORY = 'Overig'

const router = useRouter()
const dialog = useDynamicDialog()
const confirm = useConfirm()
const toast = useToast()
const queryClient = useQueryClient()

const search = ref('')
const authReady = useAuthReady()

const { data: itemsData, isPending, isError } = useAdminListLinkItems(
  computed(() => ({ limit: 200 })),
  {
    query: {
      staleTime: 30 * 1000,
      enabled: authReady,
    }, 
  },
)
const { data: categoriesData } = useAdminListLinkCategories({
  query: {
    staleTime: 30 * 1000,
    enabled: authReady,
  }, 
})

const allItems = computed(() => itemsData.value?.items ?? [])
const categories = computed<CategoryDto[]>(() => categoriesData.value?.categories ?? [])

type Group = {
  subcategory: string,
  items: LinkItemDto[] 
}
type CategoryBlock = {
  category: CategoryDto,
  groups: Group[] 
}

/** Items grouped per category → subcategory, ordered by the stored SubcategoryOrder / ItemOrder. */
const blocks = computed<CategoryBlock[]>(() =>
  categories.value
    .map((category) => {
      const grouped = new Map<string, {
        order: number,
        entries: {
          item: LinkItemDto,
          order: number 
        }[] 
      }>()

      for (const item of allItems.value) {
        const link = item.categories.find(c => c.id === category.id)
        if (!link) continue

        const subcategory = link.subcategory?.trim() || LOOSE_SUBCATEGORY
        const bucket = grouped.get(subcategory) ?? {
          order: link.subcategoryOrder,
          entries: [],
        }

        bucket.entries.push({
          item,
          order: link.itemOrder,
        })
        grouped.set(subcategory, bucket)
      }

      const groups = [ ...grouped.entries() ]
        .sort(([ aName, a ], [ bName, b ]) => a.order - b.order || aName.localeCompare(bName))
        .map(([ subcategory, bucket ]) => ({
          subcategory,
          items: bucket.entries.sort((x, y) => x.order - y.order).map(e => e.item),
        }))

      return {
        category,
        groups,
      }
    })
    .filter(block => block.groups.length > 0),
)

const isReordering = computed(() => search.value.trim().length === 0)

/** Search-filtered view (reordering is disabled while filtering). */
const visibleBlocks = computed<CategoryBlock[]>(() => {
  const term = search.value.trim().toLowerCase()
  if (!term) return blocks.value

  return blocks.value
    .map(block => ({
      category: block.category,
      groups: block.groups
        .map(group => ({
          subcategory: group.subcategory,
          items: group.items.filter(i => `${i.name} ${i.description ?? ''}`.toLowerCase().includes(term)),
        }))
        .filter(group => group.items.length > 0),
    }))
    .filter(block => block.groups.length > 0)
})

const itemsWithoutCategory = computed(() =>
  allItems.value.filter(item => item.categories.length === 0),
)

const { mutateAsync: deleteLinkItem } = useAdminDeleteLinkItem()
const { mutateAsync: reorderCategories } = useAdminReorderLinkCategories()
const { mutateAsync: reorderContents } = useAdminReorderCategoryContents()

async function invalidate() {
  await Promise.all([ queryClient.invalidateQueries({ queryKey: getAdminListLinkItemsQueryKey() }), queryClient.invalidateQueries({ queryKey: getAdminListLinkCategoriesQueryKey() }) ])
}

// --- Reordering ---
async function moveCategory(block: CategoryBlock, direction: -1 | 1) {
  const blockIds = blocks.value.map(b => b.category.id)
  const index = blockIds.indexOf(block.category.id)
  const targetId = blockIds[index + direction]
  if (targetId === undefined) return

  const ids = categories.value.map(c => c.id)
  const i = ids.indexOf(block.category.id)
  const j = ids.indexOf(targetId)

  ;[ ids[i], ids[j] ] = [ ids[j], ids[i] ]

  await reorderCategories({ data: { categoryIds: ids } })
  await invalidate()
}

async function postContents(categoryId: number, groups: Group[]) {
  await reorderContents({
    categoryId,
    data: {
      groups: groups.map(group => ({
        subcategory: group.subcategory === LOOSE_SUBCATEGORY ? null : group.subcategory,
        itemIds: group.items.map(i => i.id),
      })),
    },
  })
  await invalidate()
}

async function moveSubcategory(block: CategoryBlock, groupIndex: number, direction: -1 | 1) {
  const target = groupIndex + direction
  if (target < 0 || target >= block.groups.length) return

  const groups = block.groups.map(g => ({ ...g }))

  ;[ groups[groupIndex], groups[target] ] = [ groups[target], groups[groupIndex] ]

  await postContents(block.category.id, groups)
}

async function moveItem(block: CategoryBlock, groupIndex: number, itemIndex: number, direction: -1 | 1) {
  const group = block.groups[groupIndex]
  const target = itemIndex + direction
  if (target < 0 || target >= group.items.length) return

  const groups = block.groups.map(g => ({
    ...g,
    items: [ ...g.items ],
  }))
  const items = groups[groupIndex].items

  ;[ items[itemIndex], items[target] ] = [ items[target], items[itemIndex] ]

  await postContents(block.category.id, groups)
}

// --- CRUD ---
function openForm(item?: LinkItemDto) {
  dialog.open(LinkItemFormDialog, {
    props: {
      header: item ? 'Item bewerken' : 'Nieuw item',
      modal: true,
    },
    data: { item },
  })
}

function viewItem(item: LinkItemDto) {
  if (item.type === LinkItemType.InternalLink) {
    if (item.routeName) router.push({ name: item.routeName as any })

    return
  }

  if (item.type === LinkItemType.Uri) {
    if (item.uri) window.open(item.uri, '_blank', 'noopener')

    return
  }

  router.push({
    name: '/dashboard/DashboardDetail' as any,
    params: { id: item.id },
  })
}

function confirmDelete(item: LinkItemDto) {
  confirm.require({
    header: 'Item verwijderen',
    message: `Wil je "${item.name}" verwijderen?`,
    icon: 'fa-regular fa-triangle-exclamation',
    acceptLabel: 'Verwijderen',
    rejectLabel: 'Annuleren',
    accept: async () => {
      try {
        await deleteLinkItem({ id: item.id })
        toast.success('Verwijderd', 'Het item is verwijderd')
        await invalidate()
      }
      catch {
        toast.error('Fout', 'Er is een fout opgetreden bij het verwijderen')
      }
    },
  })
}

// Per-row actions menu.
const menu = ref()
const selectedItem = ref<LinkItemDto | null>(null)

const menuItems = ref([
  {
    label: 'Inzien',
    icon: 'fa-regular fa-eye',
    command: () => viewItem(selectedItem.value!),
  },
  {
    label: 'Bewerken',
    icon: 'fa-regular fa-pen',
    command: () => openForm(selectedItem.value!),
  },
  { separator: true },
  {
    label: 'Verwijderen',
    icon: 'fa-regular fa-trash',
    command: () => confirmDelete(selectedItem.value!),
  },
])

function openRowMenu(event: MouseEvent, item: LinkItemDto) {
  selectedItem.value = item
  menu.value.show(event)
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <Menu ref="menu" :model="menuItems" :popup="true" />
    
    <div class="flex flex-wrap items-center justify-between gap-3">
      <IconField>
        <InputIcon class="fa-regular fa-search" />
        <InputText v-model="search" placeholder="Zoeken..." size="small" />
      </IconField>

      <Button
        label="Nieuw item"
        icon="fa-regular fa-plus"
        size="small"
        @click="openForm()"
      />
    </div>

    <Message v-if="isReordering" severity="secondary" size="small">
      Sleep niet — gebruik de pijltjes rechts om categorieën, subcategorieën en items te ordenen.
      De volgorde geldt voor het menu en de homepage.
    </Message>

    <div v-if="isPending" class="flex flex-col gap-2">
      <Skeleton v-for="n in 6" :key="n" height="3rem" />
    </div>
    <Message v-else-if="isError" severity="error">
      Er is iets misgegaan bij het laden van de items.
    </Message>
    <Message v-else-if="visibleBlocks.length === 0" severity="info">
      Geen items gevonden.
    </Message>

    <template v-else>
      <Card v-for="(block, blockIndex) in visibleBlocks" :key="block.category.id">
        <template #content>
          <!-- Category header -->
          <div class="flex items-center justify-between gap-2 border-b border-surface-200 pb-2">
            <h3 class="m-0 text-base text-surface-900 font-semibold">
              {{ block.category.name }}
            </h3>
            <div v-if="isReordering" class="flex items-center gap-1">
              <Button
                icon="fa-regular fa-chevron-up"
                text
                rounded
                size="small"
                severity="secondary"
                :disabled="blockIndex === 0"
                aria-label="Categorie omhoog"
                @click="moveCategory(block, -1)"
              />
              <Button
                icon="fa-regular fa-chevron-down"
                text
                rounded
                size="small"
                severity="secondary"
                :disabled="blockIndex === visibleBlocks.length - 1"
                aria-label="Categorie omlaag"
                @click="moveCategory(block, 1)"
              />
            </div>
          </div>

          <!-- Subcategory groups -->
          <div v-for="(group, groupIndex) in block.groups" :key="group.subcategory" class="mt-3">
            <div class="flex items-center justify-between gap-2">
              <span class="text-xs text-surface-500 font-semibold tracking-wide uppercase">
                {{ group.subcategory }}
              </span>
              <div v-if="isReordering" class="flex items-center gap-1">
                <Button
                  icon="fa-regular fa-chevron-up"
                  text
                  rounded
                  size="small"
                  severity="secondary"
                  :disabled="groupIndex === 0"
                  aria-label="Subcategorie omhoog"
                  @click="moveSubcategory(block, groupIndex, -1)"
                />
                <Button
                  icon="fa-regular fa-chevron-down"
                  text
                  rounded
                  size="small"
                  severity="secondary"
                  :disabled="groupIndex === block.groups.length - 1"
                  aria-label="Subcategorie omlaag"
                  @click="moveSubcategory(block, groupIndex, 1)"
                />
              </div>
            </div>

            <!-- Item rows -->
            <div
              v-for="(item, itemIndex) in group.items"
              :key="item.id"
              class="flex items-center gap-3 border-b border-surface-100 py-2 last:border-b-0"
            >
              <i :class="item.icon || linkItemTypeIcon(item.type)" class="text-surface-400" />
              <div class="min-w-0 flex flex-1 flex-col">
                <span class="truncate text-surface-900 font-medium" :title="item.name">{{ item.name }}</span>
                <span class="text-xs text-surface-400">{{ linkItemTypeLabel(item.type) }}</span>
              </div>

              <div class="hidden flex-wrap gap-1 md:flex">
                <Tag
                  v-for="category in item.categories"
                  :key="category.id"
                  :value="category.name"
                  severity="secondary"
                />
              </div>

              <div v-if="isReordering" class="flex items-center gap-1">
                <Button
                  icon="fa-regular fa-chevron-up"
                  text
                  rounded
                  size="small"
                  severity="secondary"
                  :disabled="itemIndex === 0"
                  aria-label="Item omhoog"
                  @click="moveItem(block, groupIndex, itemIndex, -1)"
                />
                <Button
                  icon="fa-regular fa-chevron-down"
                  text
                  rounded
                  size="small"
                  severity="secondary"
                  :disabled="itemIndex === group.items.length - 1"
                  aria-label="Item omlaag"
                  @click="moveItem(block, groupIndex, itemIndex, 1)"
                />
              </div>

              <Button
                icon="fa-regular fa-ellipsis-vertical"
                text
                rounded
                size="small"
                severity="secondary"
                aria-label="Acties"
                @click="openRowMenu($event, item)"
              />
            </div>
          </div>
        </template>
      </Card>

      <Message v-if="itemsWithoutCategory.length > 0" severity="warn" size="small">
        {{ itemsWithoutCategory.length }} item(s) hebben nog geen categorie en verschijnen niet in een groep.
        Bewerk ze om een categorie toe te wijzen.
      </Message>
    </template>
  </div>
</template>
